import { styled, css } from '@withjoy/joykit/theme';
import { animationTransition } from '../../../../utils/animationTransitions';
import type { SegmentedControllerVariant } from '../../SegmentedController.types';

const underlineVariant = css`
  height: 48px;
  padding: 0 16px;
  border-radius: 0;
  color: ${({ theme }) => theme.colors.mono8};
  flex: 0 0 auto;
`;

const pillVariant = css`
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.mono10};
  min-width: ${({ isMobile }: { isMobile?: boolean }) => (isMobile ? '152px' : 'initial')};
  flex: 1;
`;

/**
 * Rendered as a real `<button type="button">`. It used to be a `TextV2 as="a"` with no `href` and no
 * `tabIndex`, which made the whole control unreachable by keyboard (WCAG 2.1.1) -- so the reset
 * below undoes the UA button chrome the element brings with it.
 */
export const ToggleWrapper = styled.div<{ isMobile?: boolean; variant?: SegmentedControllerVariant }>`
  appearance: none;
  background: none;
  border: none;
  margin: 0;
  font: inherit;

  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${animationTransition('color', { duration: '150ms' })};
  text-decoration: none;
  cursor: pointer;

  ${({ variant }) => (variant === 'underline' ? underlineVariant : pillVariant)}

  &.active {
    color: ${({ theme }) => theme.colors.mono14};
  }

  &.disabled,
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /*
   * Drawn *inside* the item. The items butt up against each other with no gap, and the pill variant
   * has only 4px of container padding around them, so a ring outside the item would overlap its
   * neighbours and reach the container's edge. Inset, it traces the item in both variants.
   * See doc/field-focus-consistency/PLAN.md.
   */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.inputBorderFocused};
    outline-offset: -2px;
  }
`;
